import * as React from 'react'

import { Box, CounterBadge, Icon, Text, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Popover from 'modules/core/components/Popover'
import type { Attachment } from 'modules/core/components/TetherComponent/types'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import FilterPanel from './FilterPanel'
import type LogicOperatorForm from './Forms/LogicOperatorForm'
import type MultiFilterForm from './Forms/MultiFilterForm'
import { FilterOption } from './types'

interface Props {
  label: string
  emptyStateLabel: string
  attachment?: Attachment
  fields: readonly FilterOption[]
  filters: MultiFilterForm[]
  logicOperator: LogicOperatorForm
  disabled?: boolean
  /**
   * Use a more minimal representation for a modern look.
   */
  dense?: boolean
  onAddFilter: () => void
  onRemoveFilter: (index: number) => void
  onApplyFilters: () => void
  onCancel: () => void
  setChildPopoverOpen?: (childPopoverOpen: boolean) => void
}

const MultiFilters = ({
  label,
  emptyStateLabel,
  attachment = 'top left',
  fields,
  filters,
  logicOperator,
  disabled,
  dense,
  onAddFilter,
  onRemoveFilter,
  onApplyFilters,
  onCancel,
  setChildPopoverOpen,
}: Props) => {
  const [open, toggle] = useToggle(false)
  const [count, setCount] = React.useState(filters.length)

  const onToggle = () => {
    if (disabled) return Promise.resolve

    return toggle(!open)
  }

  const handleOnApplyFilters = () => {
    toggle(false)()
    onApplyFilters()
    setCount(filters.length)
  }

  const handleOnCancel = () => {
    toggle(false)()
    onCancel()
  }

  useUnsafeEffect(() => {
    if (setChildPopoverOpen) setChildPopoverOpen(open)
  }, [open, setChildPopoverOpen])

  function backgroundColor() {
    if (disabled) return 'grey200'

    return 'grey000White'
  }

  const icon = open ? (dense ? 'chevron-up' : 'caret-up') : dense ? 'chevron-down' : 'caret-down'
  // if this popover is nested within another, the setChildPopoverOpen function is sent
  // as a prop and used to keep the parent popover open when the child is open
  return (
    <Popover
      open={open}
      toggle={onToggle}
      attachment={attachment}
      size="auto"
      offset="0 66px"
      disableOutsideClick
    >
      <Box
        onClick={toggle(true)}
        flexDirection="row"
        alignItems="center"
        justifyContent="spaceBetween"
        background={backgroundColor()}
        border={{
          all: {
            width: 's2',
            style: 'solid',
            color: 'grey400',
          },
        }}
        borderRadius={{
          all: 'abs008',
        }}
        cursor={disabled ? 'default' : 'pointer'}
        {...(dense
          ? {
              paddingX: 's12',
              paddingY: 's6',
            }
          : {
              padding: 's8',
              width: 's140',
            })}
      >
        <Box flexDirection="row" gap="s6">
          <CounterBadge
            count={count}
            noCountIcon={dense ? undefined : 'filter'}
            disabled={disabled}
          />
          <Text size="300" color={disabled ? 'grey800' : 'grey1000'}>
            {label}
          </Text>
        </Box>
        <Icon.Small icon={icon} />
      </Box>

      <FilterPanel
        emptyStateLabel={emptyStateLabel}
        fields={fields}
        filters={filters}
        logicOperator={logicOperator}
        onAddFilter={onAddFilter}
        onRemoveFilter={onRemoveFilter}
        onApplyFilters={handleOnApplyFilters}
        onCancel={handleOnCancel}
      />
    </Popover>
  )
}

export default observer(MultiFilters)
